import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { 
  simulateSetlistUpdate, 
  shouldRunSimulation, 
  DEFAULT_SIMULATION_PARAMS 
} from '../../utils/setlistSimulationAdvanced';
import { getTop20Titles } from '../../data/setlistStore';

/**
 * Background service that runs automatic setlist simulation.
 * Now uses advanced simulation engine with centralized song store.
 * Shows console logs only on admin route.
 */
export const SetlistSimulationService = () => {
  const intervalRef = useRef(null);
  const simulationStateRef = useRef(null);
  const paramsRef = useRef(DEFAULT_SIMULATION_PARAMS);
  const location = useLocation();
  const isAdminRoute = location.pathname === '/admin';
  
  useEffect(() => {
    // Show logs only on admin route
    const log = (...args) => {
      if (isAdminRoute) {
        console.log(...args);
      }
    };
    
    log('🎲 Starting advanced setlist simulation service...');
    log('📊 Using centralized setlistStore for top 20 priority list');
    
    const runSimulation = async () => {
      try {
        // Load simulation parameters
        const paramsDocRef = doc(db, 'adminSetlist', 'simulationParams');
        const paramsSnap = await getDoc(paramsDocRef);
        
        const params = paramsSnap.exists() 
          ? { ...DEFAULT_SIMULATION_PARAMS, ...paramsSnap.data() }
          : DEFAULT_SIMULATION_PARAMS;
        
        // Store params for reference
        paramsRef.current = params;
        
        // Load current setlist
        const setlistDocRef = doc(db, 'adminSetlist', 'currentSetlist');
        const setlistSnap = await getDoc(setlistDocRef);
        
        if (!setlistSnap.exists()) {
          log('⏭️ No setlist found, skipping simulation');
          return;
        }
        
        const data = setlistSnap.data();
        
        // Check if admin recently made changes
        if (!shouldRunSimulation(data.lastAdminUpdate, data.isManualOverride, params.adminOverrideDelay)) {
          const timeSince = Date.now() - new Date(data.lastAdminUpdate).getTime();
          const timeRemaining = params.adminOverrideDelay - timeSince;
          log(`⏸️ Simulation paused - admin updated ${Math.floor(timeSince/1000)}s ago, resuming in ${Math.floor(timeRemaining/1000)}s`);
          return;
        }
        
        // Run advanced simulation with centralized top 20 list
        log('🎯 Running simulation update...');
        const result = simulateSetlistUpdate(
          data.songs, 
          params, 
          getTop20Titles(), // Use centralized store
          simulationStateRef.current || {}, // Use empty object if null
          isAdminRoute // Enable debug mode on admin route
        );
        
        // Store state for next iteration
        simulationStateRef.current = result.state;
        
        // Save to Firebase - preserve existing fields, only update simulation results
        await setDoc(setlistDocRef, {
          ...data,
          songs: result.songs,
          updatedAt: new Date().toISOString(),
          updatedBy: 'simulation-advanced',
          // Don't reset isManualOverride - let it expire naturally based on lastAdminUpdate
          // Don't touch lastAdminUpdate - it should only be set by admin actions
          isActive: true
        });
        
        log('✅ Advanced simulation update complete');
        
      } catch (error) {
        if (isAdminRoute) {
          console.error('❌ Simulation error:', error);
        }
      }
    };
    
    // Initialize simulation
    const initSimulation = async () => {
      // Run immediately on mount
      await runSimulation();
      
      // Load params to get update interval
      const paramsDocRef = doc(db, 'adminSetlist', 'simulationParams');
      const paramsSnap = await getDoc(paramsDocRef);
      const params = paramsSnap.exists() 
        ? { ...DEFAULT_SIMULATION_PARAMS, ...paramsSnap.data() }
        : DEFAULT_SIMULATION_PARAMS;
      
      // Set up interval with loaded params
      if (isAdminRoute) {
        console.log(`⏰ Setting up simulation interval: ${params.updateInterval/1000}s`);
      }
      intervalRef.current = setInterval(runSimulation, params.updateInterval);
    };
    
    initSimulation();
    
    return () => {
      if (isAdminRoute) {
        console.log('🛑 Stopping simulation service');
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAdminRoute]);
  
  // This component doesn't render anything
  return null;
};

export default SetlistSimulationService;
